/**
 * Super-Admin — Datei-Speicher (eigener S3-Bucket: Infomaniak / Hetzner / Exoscale …).
 * Zugangsdaten werden erst nach erfolgreichem Verbindungstest gespeichert (Secret verschlüsselt).
 */
import React, { useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, Save, TestTube2, ExternalLink, HardDrive, CheckCircle2, AlertCircle, ArrowRightLeft } from "lucide-react";
import { formatDateTime } from "@/lib/formatDate";
import { StorageMigrationPanel } from "./StorageMigrationPanel";
import { StorageSetupGuide } from "./StorageSetupGuide";

const EMPTY = { preset: "infomaniak", endpoint_url: "https://s3.pub1.infomaniak.cloud", access_key_id: "", secret_access_key: "", bucket: "", region: "pub1" };

export default function StorageSystemConfigCard() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    setLoading(true);
    try {
      const r = await apiClient.get("/admin/storage-config");
      setCfg(r.data);
      if (r.data.configured) {
        const match = Object.entries(r.data.presets || {}).find(([, p]) => p.endpoint_url && p.endpoint_url === r.data.endpoint_url);
        setForm((f) => ({ ...f, preset: match ? match[0] : "custom", endpoint_url: r.data.endpoint_url, access_key_id: r.data.access_key_id, bucket: r.data.bucket, region: r.data.region, secret_access_key: "" }));
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speicher-Konfiguration nicht ladbar");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const applyPreset = (key) => {
    const p = cfg?.presets?.[key];
    setForm({ ...form, preset: key, endpoint_url: p?.endpoint_url ?? form.endpoint_url, region: p?.region ?? form.region });
  };

  const save = async () => {
    if (!form.endpoint_url.trim() || !form.access_key_id.trim() || !form.bucket.trim()) {
      toast.error("Endpoint, Access Key und Bucket sind erforderlich");
      return;
    }
    if (!form.secret_access_key && !cfg?.configured) {
      toast.error("Secret Key erforderlich");
      return;
    }
    setSaving(true);
    try {
      const payload = { endpoint_url: form.endpoint_url.trim(), access_key_id: form.access_key_id.trim(), bucket: form.bucket.trim(), region: form.region.trim() || "auto" };
      if (form.secret_access_key) payload.secret_access_key = form.secret_access_key;
      await apiClient.put("/admin/storage-config", payload);
      toast.success("Verbindung erfolgreich – Zugangsdaten gespeichert");
      setForm({ ...form, secret_access_key: "" });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    try {
      const r = await apiClient.post("/admin/storage-config/test");
      if (r.data.ok) toast.success(`✓ Verbunden – ${r.data.object_count} Dateien im Bucket`);
      else toast.error(r.data.error);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test fehlgeschlagen");
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <Card className="p-6 flex items-center justify-center text-slate-500">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Lade …
      </Card>
    );
  }

  return (
    <Card className="p-5" data-testid="storage-system-config-card">
      <div className="flex items-start justify-between mb-3 gap-3">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <HardDrive className="h-5 w-5 text-slate-700" />
            <span className="text-lg font-semibold text-slate-900">Datei-Speicher (eigener S3-Bucket)</span>
            {cfg?.configured && cfg?.last_test_ok ? (
              <Badge className="bg-emerald-100 text-emerald-800" data-testid="storage-status-badge">Verbunden · {cfg.object_count ?? "–"} Dateien</Badge>
            ) : cfg?.configured ? (
              <Badge className="bg-amber-100 text-amber-800" data-testid="storage-status-badge">Konfiguriert · Test ausstehend</Badge>
            ) : (
              <Badge className="bg-rose-100 text-rose-800" data-testid="storage-status-badge">Nicht konfiguriert</Badge>
            )}
            {cfg?.source === "env" && <Badge variant="outline">aus Server-Umgebung</Badge>}
          </div>
          <p className="text-sm text-slate-500 mt-1">
            Alle Belege, Rechnungs-PDFs, Lohnabrechnungen und Garantie-Dokumente werden hier abgelegt – auf deinem eigenen Speicher, kein Drittanbieter-Fallback.
          </p>
          {cfg?.last_test_at && (
            <p className="text-xs text-slate-500 mt-1 flex items-center gap-1" data-testid="storage-last-test">
              {cfg.last_test_ok ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> : <AlertCircle className="h-3.5 w-3.5 text-rose-600" />}
              Letzter Test {formatDateTime(cfg.last_test_at)}{cfg.last_test_error ? ` – ${cfg.last_test_error}` : ""}
            </p>
          )}
        </div>
      </div>

      <StorageSetupGuide />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
        <div>
          <Label htmlFor="st-preset">Anbieter</Label>
          <select
            id="st-preset"
            className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
            value={form.preset}
            onChange={(e) => applyPreset(e.target.value)}
            data-testid="storage-preset-select"
          >
            {Object.entries(cfg?.presets || {}).map(([k, p]) => <option key={k} value={k}>{p.label}</option>)}
          </select>
        </div>
        <div>
          <Label htmlFor="st-endpoint">Endpoint-URL</Label>
          <Input id="st-endpoint" value={form.endpoint_url} onChange={(e) => setForm({ ...form, endpoint_url: e.target.value })} placeholder="https://s3.pub1.infomaniak.cloud" data-testid="storage-endpoint-input" />
        </div>
        <div>
          <Label htmlFor="st-bucket">Bucket-Name</Label>
          <Input id="st-bucket" value={form.bucket} onChange={(e) => setForm({ ...form, bucket: e.target.value })} placeholder="z.B. buchhaltung-prod" data-testid="storage-bucket-input" />
        </div>
        <div>
          <Label htmlFor="st-region">Region</Label>
          <Input id="st-region" value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} placeholder="pub1" data-testid="storage-region-input" />
        </div>
        <div>
          <Label htmlFor="st-access">Access Key</Label>
          <Input id="st-access" value={form.access_key_id} onChange={(e) => setForm({ ...form, access_key_id: e.target.value })} placeholder="Access Key ID" data-testid="storage-access-key-input" />
        </div>
        <div>
          <Label htmlFor="st-secret">Secret Key {cfg?.configured && <span className="text-slate-400 font-normal">(leer = unverändert, aktuell {cfg.secret_masked})</span>}</Label>
          <Input id="st-secret" type="password" value={form.secret_access_key} onChange={(e) => setForm({ ...form, secret_access_key: e.target.value })} placeholder="Secret Access Key" data-testid="storage-secret-key-input" />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mt-4">
        <Button onClick={save} disabled={saving} data-testid="storage-save-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
          Testen &amp; speichern
        </Button>
        <Button variant="outline" onClick={test} disabled={testing || !cfg?.configured} data-testid="storage-test-btn">
          {testing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <TestTube2 className="h-4 w-4 mr-1" />}
          Verbindung testen
        </Button>
        <a href="https://manager.infomaniak.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm text-indigo-700 underline ml-auto">
          Infomaniak Manager öffnen <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </div>

      {cfg?.configured && cfg?.last_test_ok && (
        <div className="mt-5 pt-4 border-t border-slate-200">
          <div className="flex items-center gap-2 mb-2">
            <ArrowRightLeft className="h-4 w-4 text-slate-700" />
            <span className="font-medium text-slate-900">Bestehende Dateien übernehmen</span>
          </div>
          <StorageMigrationPanel initial={cfg.migration} legacyAvailable={cfg.legacy_source_available} onDone={load} />
        </div>
      )}
    </Card>
  );
}
